import type { AgentResponse } from "@/types/agents"
import type {
  AssistantSuggestion,
  AssistantSuggestionSource,
  AssistantSuggestionsResponse,
} from "@/types/assistant-suggestion"
import {
  SuggestionLabPreview,
  SuggestionLabRequest,
} from "@/types/suggestion-lab"
import { TripAdviceDismissalReason } from "@/types/trip"

import { onAssistantSuggestionsEvent } from "@/lib/assistant-suggestions-events"
import type { RootState } from "@/lib/storage/redux-store"
import { agentsApi } from "./agents-api"
import { baseApi } from "./base-api"
import { needsAttentionApi } from "./needs-attention-api"

const CUSTOM_AGENTS_QUERY = { agent_type: "custom_agent" as const }

// Whether a generate is running, from any caller. The endpoint owns it so it
// drops only after the response has settled the cached queue: a Home mount
// that saw it drop with pending_generation still up would generate again.
let generationInFlight = false
const generationListeners = new Set<() => void>()

function setGenerationInFlight(inFlight: boolean) {
  generationInFlight = inFlight
  generationListeners.forEach((listener) => listener())
}

export function subscribeToGeneration(listener: () => void) {
  generationListeners.add(listener)
  return () => {
    generationListeners.delete(listener)
  }
}

export function isGenerationInFlight() {
  return generationInFlight
}

export const assistantSuggestionsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // One read for every surface: the queue carries every topic, and each surface picks
    // the cards it renders out of it.
    getAssistantSuggestions: builder.query<AssistantSuggestionsResponse, void>({
      query: () => ({ url: "/api/assistant_suggestions" }),
      providesTags: ["AssistantSuggestions"],
      async onCacheEntryAdded(
        _arg,
        { cacheDataLoaded, cacheEntryRemoved, updateCachedData, dispatch }
      ) {
        let unsubscribe: (() => void) | undefined
        try {
          await cacheDataLoaded
          // One listener for the shared queue, including while generation's
          // slow response is pending. Cards land in arrival order; the server
          // owns ranking, and a finished generation — ours or a background
          // job's — ends with a re-read so the queue is the server's again.
          unsubscribe = onAssistantSuggestionsEvent((event) => {
            if (event.event === "complete") {
              dispatch(
                assistantSuggestionsApi.util.invalidateTags([
                  "AssistantSuggestions",
                ])
              )
              return
            }
            if (event.event !== "card") return
            updateCachedData((draft) => {
              const card = event.suggestion
              const index = draft.suggestions.findIndex(
                (known) =>
                  known.ref === card.ref || known.unique_key === card.unique_key
              )
              if (index === -1) {
                draft.suggestions.push(card)
              } else {
                const known = draft.suggestions[index]
                // Racing generations can publish two refs for the same key;
                // the server serves the one with the highest priority.
                if (known.ref !== card.ref && known.priority > card.priority)
                  return
                draft.suggestions[index] = card
              }
            })
          })
          await cacheEntryRemoved
        } catch {
          // The cache entry may be removed before the initial read finishes.
        } finally {
          unsubscribe?.()
        }
      },
    }),
    // Resolve source action context on click; lists carry only the card and pills.
    getAssistantSuggestionSource: builder.query<
      AssistantSuggestionSource,
      string
    >({
      query: (ref) => ({ url: `/api/assistant_suggestions/${ref}/source` }),
      // Tagged per card: settling a row the card stands for changes what its source holds,
      // and with no tag at all nothing could ever refresh it.
      providesTags: (_result, _error, ref) => [
        { type: "AssistantSuggestions", id: ref },
      ],
    }),
    // The read is a fast diff; when it reports pending_generation the queue
    // has unmaterialized cards and this mutation makes them (server-side
    // batched LLM call), returning the refreshed queue.
    generateAssistantSuggestions: builder.mutation<
      AssistantSuggestionsResponse,
      void
    >({
      query: () => ({
        url: "/api/assistant_suggestions/generate",
        method: "POST",
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        setGenerationInFlight(true)
        try {
          const { data } = await queryFulfilled
          await dispatch(
            assistantSuggestionsApi.util.upsertQueryData(
              "getAssistantSuggestions",
              undefined,
              { ...data, pending_generation: false }
            )
          )
        } catch {
          // A failed attempt leaves the server's pending flag in the cache, so
          // the next Home mount takes its own attempt.
        } finally {
          setGenerationInFlight(false)
        }
      },
    }),
    // /internal/suggestion-lab: a dry run from a pretend spot. Nothing is saved
    // but the AuditLog row the response names.
    previewLabSuggestion: builder.mutation<
      SuggestionLabPreview,
      SuggestionLabRequest
    >({
      query: (data) => ({
        url: "/api/assistant_suggestions/lab_preview",
        method: "POST",
        data,
      }),
    }),
    // Create the agent and record engagement; single-pill cards auto-dismiss.
    createSuggestionAgent: builder.mutation<AgentResponse, string>({
      query: (ref) => ({
        url: `/api/assistant_suggestions/${ref}/create_agent`,
        method: "POST",
      }),
      invalidatesTags: ["AssistantSuggestions"],
      async onQueryStarted(ref, { dispatch, queryFulfilled }) {
        let removed: AssistantSuggestion | undefined
        const suggestionsPatch = dispatch(
          assistantSuggestionsApi.util.updateQueryData(
            "getAssistantSuggestions",
            undefined,
            (draft) => {
              const index = draft.suggestions.findIndex(
                (suggestion) => suggestion.ref === ref
              )
              if (index === -1) return
              removed = draft.suggestions[index]
              draft.suggestions.splice(index, 1)
            }
          )
        )

        try {
          const { data } = await queryFulfilled
          dispatch(
            agentsApi.util.updateQueryData(
              "getAgents",
              CUSTOM_AGENTS_QUERY,
              (draft) => {
                if (
                  draft.agents.some((agent) => agent.ref === data.agent.ref)
                ) {
                  return
                }
                draft.agents.unshift(data.agent)
              }
            )
          )
        } catch {
          if (removed) suggestionsPatch.undo()
        }
      },
    }),
    // A view means the card dwelled on screen; the server keeps a first/last
    // viewed span and retires cards that sit unread. Nothing to refetch.
    recordSuggestionView: builder.mutation<void, { ref: string }>({
      query: ({ ref }) => ({
        url: `/api/assistant_suggestions/${ref}/view`,
        method: "POST",
      }),
    }),
    // Engagement and card visibility are handled independently on the server.
    recordSuggestionPillClick: builder.mutation<
      void,
      { ref: string; pillRef?: string }
    >({
      query: ({ ref, pillRef }) => ({
        url: `/api/assistant_suggestions/${ref}/pill_click`,
        method: "POST",
        data: { pill_ref: pillRef },
      }),
      invalidatesTags: ["AssistantSuggestions"],
    }),
    // Dismissal is one gesture against both systems: the server also records
    // the response on a wrapper card's source (e.g. the trip advice), so its
    // feedback loop learns too.
    dismissAssistantSuggestion: builder.mutation<
      void,
      { ref: string; reason: TripAdviceDismissalReason; note?: string }
    >({
      query: ({ ref, reason, note }) => ({
        url: `/api/assistant_suggestions/${ref}/dismiss`,
        method: "POST",
        data: { reason, ...(note ? { note } : {}) },
      }),
      invalidatesTags: ["AssistantSuggestions"],
      // A topic's on_dismiss may settle its source — dismissing a disruption marks its rows
      // done — and needs-attention is a separate slice, so its tags do not cross. The socket
      // broadcast covers this too, but only while subscribed to the trip that changed.
      async onQueryStarted({ ref }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          assistantSuggestionsApi.util.updateQueryData(
            "getAssistantSuggestions",
            undefined,
            (draft) => {
              const index = draft.suggestions.findIndex(
                (suggestion) => suggestion.ref === ref
              )
              if (index !== -1) draft.suggestions.splice(index, 1)
            }
          )
        )
        try {
          await queryFulfilled
        } catch {
          patch.undo()
          return
        }
        void dispatch(needsAttentionApi.util.invalidateTags(["NeedsAttention"]))
      },
    }),
  }),
})

export const {
  useGetAssistantSuggestionsQuery,
  useGetAssistantSuggestionSourceQuery,
  useLazyGetAssistantSuggestionSourceQuery,
  useGenerateAssistantSuggestionsMutation,
  useCreateSuggestionAgentMutation,
  useRecordSuggestionViewMutation,
  useRecordSuggestionPillClickMutation,
  useDismissAssistantSuggestionMutation,
  usePreviewLabSuggestionMutation,
} = assistantSuggestionsApi

/** The queue is already in the cache: a page arriving to it has no load to cover. */
export const selectAssistantSuggestionsCached = (state: RootState) =>
  !!assistantSuggestionsApi.endpoints.getAssistantSuggestions.select()(state)
    .data
